import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

const MarkdownContent = styled.div<{ $fontSize: string }>`
  line-height: 1.6;
  font-size: ${props => props.$fontSize};
  overflow-wrap: break-word;
  width: 100%;
  color: rgba(255, 255, 255, 0.95);

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    color: rgba(255, 255, 255, 1);
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-weight: 600;
    line-height: 1.3;
  }

  /* Sized in em so headings scale with $fontSize. The ratios reproduce the
     previous rem values at the default 0.75rem base, so existing callers are
     unchanged; larger bases now scale headings instead of leaving h3 smaller
     than body text. */
  h1 { font-size: 1.667em; }
  h2 { font-size: 1.467em; }
  h3 { font-size: 1.333em; }
  h4 { font-size: 1.2em; }
  h5 { font-size: 1.133em; }
  h6 { font-size: 1.067em; }

  /* Paragraphs */
  p {
    margin: 0.75rem 0;
  }

  /* Inline code */
  code:not(pre code) {
    background: rgba(255, 255, 255, 0.15);
    padding: 0.15rem 0.35rem;
    border-radius: 4px;
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 0.9em;
    color: #e6db74;
  }

  /* Code blocks */
  pre {
    background: rgba(20, 20, 25, 0.95);
    padding: 0.75rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.75rem 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

    code {
      background: none;
      padding: 0;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 0.9em;
      line-height: 1.5;
      color: #f8f8f2;
    }
  }

  /* Math equations */
  .katex {
    font-size: 1.05em;
    color: rgba(255, 255, 255, 0.95);
  }

  .katex-display {
    margin: 1rem 0;
    overflow-x: auto;
    padding: 0.5rem 0;
  }

  /* Lists */
  ul, ol {
    margin-left: 2rem;
    margin: 0.75rem 0;
  }

  li {
    margin: 0.35rem 0;
    line-height: 1.6;
  }

  li > ul,
  li > ol {
    margin-top: 0.35rem;
    margin-bottom: 0.35rem;
  }

  /* Strong and emphasis */
  strong {
    font-weight: 600;
    color: rgba(255, 255, 255, 1);
  }

  em {
    font-style: italic;
  }

  /* Blockquotes */
  blockquote {
    border-left: 4px solid rgba(139, 92, 246, 0.6);
    padding-left: 1rem;
    margin: 1rem 0;
    color: rgba(255, 255, 255, 0.8);
    font-style: italic;
  }

  /* Horizontal rules */
  hr {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    margin: 1.5rem 0;
  }

  /* Tables */
  table {
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    margin: 1rem 0;
    display: table;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  thead {
    display: table-header-group;
  }

  tbody {
    display: table-row-group;
  }

  tr {
    display: table-row;
  }

  th, td {
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 0.5rem 0.75rem;
    text-align: left;
    font-size: 1em;
    display: table-cell;
    vertical-align: top;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  th {
    background: rgba(255, 255, 255, 0.1);
    font-weight: 600;
    color: rgba(255, 255, 255, 1);
  }

  td {
    color: rgba(255, 255, 255, 0.9);
  }

  /* Table wrapper for overflow handling */
  table {
    position: relative;
  }

  /* Ensure table doesn't overflow */
  table * {
    box-sizing: border-box;
  }

  /* Links */
  a {
    color: #a78bfa;
    text-decoration: none;
    transition: color 0.2s ease;
  }

  a:hover {
    color: #c4b5fd;
    text-decoration: underline;
  }

  /* Images */
  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 0.5rem 0;
  }

  /* Syntax highlighting overrides */
  .syntax-highlighter {
    background: transparent !important;
    border-radius: 8px;

    * {
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace !important;
    }
  }
`;

interface MarkdownRendererProps {
  content: string;
  /** Base font size for the rendered Markdown. Defaults to the compact chat size. */
  fontSize?: string;
}

const CodeBlock = ({ className, children }: { className?: string; children: React.ReactNode }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (language && typeof children === 'string') {
    return (
      <div className="syntax-highlighter">
        <SyntaxHighlighter
          style={oneDark as any}
          language={language}
          PreTag="div"
          customStyle={{
            background: 'transparent',
            padding: '0',
            margin: '0',
          } as any}
        >
          {children.replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    );
  }

  return (
    <code className={className}>
      {children}
    </code>
  );
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, fontSize = '0.75rem' }) => {
  return (
    <MarkdownContent $fontSize={fontSize}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          code: CodeBlock as any,
        }}
      >
        {content}
      </ReactMarkdown>
    </MarkdownContent>
  );
};
